import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../lib/api";
import { LANG_NAMES, pageLang, voiceFor, collectBlocks, buildChunks, cloudChunks } from "./speech";

/* Listen engine — plays a page aloud via the device Web Speech API, falling back to
   Cloud Voices (server AI narration) for languages without a built-in voice.
   Highlights the paragraph being read; tapping any paragraph jumps there. */
export function useListen(targetId) {
  const [state, setState] = useState("idle"); // idle | playing | paused
  const [rate, setRate] = useState(() => Number(localStorage.getItem("tp_tts_rate")) || 0.9);
  const chunksRef = useRef([]); // [{text, block}]
  const blocksRef = useRef([]);
  const idxRef = useRef(0);
  const sessionRef = useRef(0); // incremented on stop/jump so stale onend callbacks are ignored
  const modeRef = useRef("device"); // device (Web Speech) | cloud (AI narration)
  const audioRef = useRef(null);
  const hlRef = useRef(null);
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;

  const clearHl = () => {
    if (hlRef.current) { hlRef.current.classList.remove("tts-reading"); hlRef.current = null; }
    document.getElementById(targetId)?.classList.remove("tts-session");
  };

  // warm the async voice list so translated-page checks are accurate
  useEffect(() => {
    if (!supported) return undefined;
    window.speechSynthesis.getVoices();
    const noop = () => {};
    window.speechSynthesis.addEventListener?.("voiceschanged", noop);
    return () => window.speechSynthesis.removeEventListener?.("voiceschanged", noop);
  }, [supported]);

  useEffect(() => () => {
    if (supported) { sessionRef.current += 1; window.speechSynthesis.cancel(); }
    audioRef.current?.pause();
    clearHl();
  }, [supported]); // eslint-disable-line react-hooks/exhaustive-deps

  const highlight = (block) => {
    if (hlRef.current === block) return;
    if (hlRef.current) hlRef.current.classList.remove("tts-reading");
    hlRef.current = block || null;
    if (block) {
      block.classList.add("tts-reading");
      try { block.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch { /* older browsers */ }
    }
  };

  /* ---- Device voice (Web Speech) ---- */
  const speakNext = (session) => {
    if (session !== sessionRef.current) return;
    if (idxRef.current >= chunksRef.current.length) { setState("idle"); clearHl(); return; }
    const chunk = chunksRef.current[idxRef.current];
    highlight(chunk.block);
    const u = new SpeechSynthesisUtterance(chunk.text);
    u.rate = rate;
    const lang = pageLang();
    if (lang !== "en") {
      u.lang = document.documentElement.lang || lang;
      const v = voiceFor(lang);
      if (v && v !== "unknown") u.voice = v;
    }
    u.onend = () => { if (session === sessionRef.current) { idxRef.current += 1; speakNext(session); } };
    u.onerror = () => {
      if (session !== sessionRef.current) return;
      // Device engine failed on the very first chunk (e.g. zero installed voices,
      // common on kiosk/managed devices) -> fall back to Cloud Voices seamlessly.
      if (idxRef.current === 0 && modeRef.current === "device") {
        startCloud(Math.max(0, blocksRef.current.indexOf(chunk.block)));
        return;
      }
      setState("idle"); clearHl();
    };
    window.speechSynthesis.speak(u);
  };

  /* ---- Cloud Voices: AI narration (server TTS) ---- */
  const playCloudNext = async (session) => {
    if (session !== sessionRef.current) return;
    if (idxRef.current >= chunksRef.current.length) { setState("idle"); clearHl(); return; }
    const chunk = chunksRef.current[idxRef.current];
    highlight(chunk.block);
    try {
      const r = await axios.post(`${API}/tts/generate`, { text: chunk.text });
      if (session !== sessionRef.current) return;
      const url = r.data.url.startsWith("/api") ? API + r.data.url.slice(4) : r.data.url;
      const a = audioRef.current || new Audio();
      audioRef.current = a;
      a.src = url;
      a.onended = () => { if (session === sessionRef.current) { idxRef.current += 1; playCloudNext(session); } };
      a.onerror = () => { if (session === sessionRef.current) { setState("idle"); clearHl(); } };
      await a.play();
    } catch (e) {
      if (session === sessionRef.current) {
        toast.error(e?.response?.data?.detail || "AI narration unavailable right now — please try again");
        setState("idle"); clearHl();
      }
    }
  };

  const startCloud = (blockIdx) => {
    const el = document.getElementById(targetId);
    if (!el) return;
    sessionRef.current += 1;
    const session = sessionRef.current;
    window.speechSynthesis.cancel();
    audioRef.current?.pause();
    blocksRef.current = collectBlocks(el);
    chunksRef.current = cloudChunks(blocksRef.current);
    if (!chunksRef.current.length) return;
    const target = blocksRef.current[blockIdx];
    idxRef.current = blockIdx > 0 && target ? Math.max(0, chunksRef.current.findIndex((c) => c.block === target)) : 0;
    modeRef.current = "cloud";
    el.classList.add("tts-session");
    setState("playing");
    const name = LANG_NAMES[pageLang()] || "this language";
    toast.info(`Using AI narration for ${name}`, { description: "Your device has no built-in voice for this language — AirlinesAirports generates the audio for you. First paragraphs take a few seconds.", duration: 5000 });
    playCloudNext(session);
  };

  const startFrom = (blockIdx) => {
    const el = document.getElementById(targetId);
    if (!el) return;
    // Translated page with no matching device voice -> Cloud Voices (AI narration)
    const lang = pageLang();
    if (lang !== "en" && voiceFor(lang) === null) {
      startCloud(blockIdx);
      return;
    }
    sessionRef.current += 1;
    const session = sessionRef.current;
    window.speechSynthesis.cancel();
    blocksRef.current = collectBlocks(el);
    chunksRef.current = buildChunks(blocksRef.current);
    if (!chunksRef.current.length) return;
    const target = blocksRef.current[blockIdx];
    idxRef.current = blockIdx > 0 && target ? Math.max(0, chunksRef.current.findIndex((c) => c.block === target)) : 0;
    modeRef.current = "device";
    el.classList.add("tts-session");
    setState("playing");
    speakNext(session);
  };

  // tap any paragraph to read from there (only while a listening session is active)
  useEffect(() => {
    if (state === "idle") return undefined;
    const el = document.getElementById(targetId);
    const jump = (e) => {
      if (!el || !el.contains(e.target)) return;
      const idx = blocksRef.current.findIndex((b) => b.contains(e.target));
      if (idx >= 0) startFrom(idx);
    };
    el?.addEventListener("click", jump);
    return () => el?.removeEventListener("click", jump);
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    if (state === "idle") return startFrom(0);
    const cloud = modeRef.current === "cloud";
    if (state === "playing") { cloud ? audioRef.current?.pause() : window.speechSynthesis.pause(); setState("paused"); }
    else { cloud ? audioRef.current?.play() : window.speechSynthesis.resume(); setState("playing"); }
  };

  const stop = () => {
    sessionRef.current += 1;
    window.speechSynthesis.cancel();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.removeAttribute("src"); }
    setState("idle"); clearHl();
  };

  const setRateStored = (v) => { setRate(v); localStorage.setItem("tp_tts_rate", String(v)); };

  return { supported, state, rate, setRate: setRateStored, toggle, stop, modeRef };
}
